import { ArrowLeftOutlined } from '@ant-design/icons';
import { EditDetailTestTemplateView, ModePage, QuestionType, ThinkingLevel } from 'common/enum';
import { BaseTreeItem } from 'common/type/import';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditExamView from './EditExamView';
import EditExamPartView from './EditExamPartView';
import EditExamPartQuestionView from './EditExamPartQuestionView';
import { message, Popover } from 'antd';
import { ExamImportRequest, ExamPartImportRequest, ExamPartQuestionImportRequest } from '../../../model/request';
import {
  useAppConfigIsLoading,
  useExamEditErrorSelector,
  useExamEditExamRequestSelector,
  useExamEditSubjectSelector,
  useMasterSubjectsSelector,
} from '../../../redux/selector';
import ExamMenuItem from './ExamMenuItem';
import { convertToRoman, uuidv4 } from 'common/commonFunc';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store/store';
import { setLoading, updateLoading } from '../../../redux/slice/appConfigSlice';
import { initExamEdit, updateExamImportError, updateExamPartRequests } from '../../../redux/slice/examEditSlice';
import { cloneDeep } from 'lodash';
import processError from '../../../utils/processError';
import { getFullExamDetail, saveExamRequest } from '../../../service/examService';
import { findExamTemplatesById } from '../../../service/examTemplateService';
import { ExamPartQuestionErrorCode } from '../../../model/enum';
import ErrorView from '../../common/ErrorView';
import {
  checkIfExamRequestHasAnyError,
  checkIsEnglishSubject,
  processExamPartQuestionRequestIndexMap,
} from '../../../utils/utils';
import { getFullPracticeDetail } from '../../../service/practiceService';

type EditTemplateViewProps = {
  moveToSelectFileView?: () => void;
  type?: 'exam' | 'practice';
};

export default function EditTemplateView({ moveToSelectFileView, type = 'exam' }: EditTemplateViewProps) {
  const navigate = useNavigate();
  const params = useParams();
  const examRequest = useExamEditExamRequestSelector();
  const examImportError = useExamEditErrorSelector();
  const isLoading = useAppConfigIsLoading();
  const subjects = useMasterSubjectsSelector();
  const examEditSubject = useExamEditSubjectSelector();
  const [modeEditView, setModeEditView] = useState<EditDetailTestTemplateView | null>(null);
  const [isEditGroupQuestion, setIsEditGroupQuestion] = useState<boolean>(false);
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const modePage = params && params.examId ? ModePage.EDIT : ModePage.IMPORT;
  const dispatch = useDispatch<AppDispatch>();

  const backStepImport = function () {
    if (modePage === ModePage.EDIT || params.subjectId) {
      navigate(`/${type}-management/subject/${params.subjectId}/${type}s`);
    } else {
      moveToSelectFileView && moveToSelectFileView();
    }
  };

  const selectEditItem = function (
    selectEditItem: ExamImportRequest | ExamPartImportRequest | ExamPartQuestionImportRequest | null,
  ) {
    if (!selectEditItem) {
      return;
    }
    setIsEditGroupQuestion(false);

    if ('examParts' in selectEditItem && selectEditItem.examParts) {
      setModeEditView(EditDetailTestTemplateView.EDIT_EXAM);
    } else if ('examPartQuestions' in selectEditItem && selectEditItem.examPartQuestions) {
      setModeEditView(EditDetailTestTemplateView.EDIT_PATH);
    } else {
      if ((selectEditItem as ExamPartQuestionImportRequest).question.type === QuestionType.GROUPED_QUESTION) {
        setIsEditGroupQuestion(true);
      }
      setModeEditView(EditDetailTestTemplateView.EDIT_SUB_QUESTION);
    }

    setEditItemId(selectEditItem.id);
  };

  const editDetailView = useMemo(() => {
    switch (modeEditView) {
      case EditDetailTestTemplateView.EDIT_EXAM:
        return <EditExamView />;
      case EditDetailTestTemplateView.EDIT_PATH:
        const examPart = examRequest?.examParts.find((examPart) => examPart.id === editItemId);
        return <EditExamPartView examPart={examPart} />;
      case EditDetailTestTemplateView.EDIT_QUESTION:
      case EditDetailTestTemplateView.EDIT_SUB_QUESTION:
        let foundExamPartQuestion: ExamPartQuestionImportRequest | null = null;
        examRequest?.examParts.forEach((examPart) => {
          examPart.examPartQuestions.forEach((examPartQuestion) => {
            if (examPartQuestion.id === editItemId) {
              foundExamPartQuestion = examPartQuestion;
            }
            examPartQuestion.children.forEach((examPartQuestion) => {
              if (examPartQuestion.id === editItemId) {
                foundExamPartQuestion = examPartQuestion;
              }
            });
          });
        });
        return <EditExamPartQuestionView examPartQuestion={foundExamPartQuestion} subject={examRequest?.subject} />;
      default:
        break;
    }
  }, [editItemId, examRequest, modeEditView]);

  const buildNewExamPartQuestion = (
    questionIndex: number,
    questionType: QuestionType,
  ): ExamPartQuestionImportRequest => {
    return {
      id: uuidv4(),
      mark: 0,
      weight: 1,
      examPartQuestionIndex: 0,
      question: {
        id: uuidv4(),
        name: '',
        code: '',
        topic: '',
        type: questionType,
        pageNumber: 0,
        thinkingLevels: [ThinkingLevel.KNOWING],
        hint: '',
        solutionGuide: '',
        image: '',
        imageSize: '',
        questionIndex,
        rightAnswer: '0',

        bookAppendix: undefined,
        skillTypes: undefined,
        parent: undefined,
        exerciseFormats: undefined,

        questionDetails: [],
        children: [],
      },
      children: [],
    };
  };

  const addPreviousExamPartQuestion = useCallback(() => {
    const examParts: ExamPartImportRequest[] = examRequest?.examParts ? cloneDeep(examRequest?.examParts) : [];
    let isFindQuestion = false;
    let isQuestionInExamPart = false;
    let newExamPartQuestion = null;
    for (let i = 0; i < examParts?.length; i++) {
      const examPartQuestions = examParts[i].examPartQuestions;
      for (let j = 0; j < examPartQuestions.length; j++) {
        if (examPartQuestions[j].id === editItemId) {
          isFindQuestion = true;
          isQuestionInExamPart = true;
          newExamPartQuestion = buildNewExamPartQuestion(j, examPartQuestions[j].question.type as QuestionType);

          examPartQuestions[j] = {
            ...examPartQuestions[j],
            question: {
              ...examPartQuestions[j].question,
              questionIndex: j + 1,
            },
          };
          examPartQuestions.splice(j, 0, newExamPartQuestion);
          j++;
        }

        if (isFindQuestion && isQuestionInExamPart) {
          examPartQuestions[j].question.questionIndex = j;
        } else if (examPartQuestions[j].question.type === QuestionType.GROUPED_QUESTION) {
          for (let z = 0; z < examPartQuestions[j].children?.length; z++) {
            if (examPartQuestions[j].children[z].id === editItemId) {
              isFindQuestion = true;
              newExamPartQuestion = buildNewExamPartQuestion(
                z,
                examPartQuestions[j].children[z].question.type as QuestionType,
              );
              examPartQuestions[j].children.splice(z, 0, newExamPartQuestion);
              z++;
            }

            if (isFindQuestion) {
              examPartQuestions[j].children[z] = {
                ...examPartQuestions[j].children[z],
                question: { ...examPartQuestions[j].children[z].question, questionIndex: z },
              };
            }
          }
        }
      }
    }

    dispatch(updateExamPartRequests(examParts));
  }, [dispatch, editItemId, examRequest?.examParts]);

  const deleteExamPartQuestion = useCallback(() => {
    const examParts: ExamPartImportRequest[] = examRequest?.examParts ? cloneDeep(examRequest.examParts) : [];
    let isFindQuestion = false;
    for (let i = 0; i < examParts?.length; i++) {
      const examPartQuestions = examParts[i].examPartQuestions;
      for (let j = 0; j < examPartQuestions.length; j++) {
        if (examPartQuestions[j].id === editItemId) {
          isFindQuestion = true;
          examPartQuestions.splice(j, 1);
          j--;
        }

        if (isFindQuestion && j >= 0) {
          examPartQuestions[j].question.questionIndex = j;
        }

        if (!isFindQuestion && examPartQuestions[j].question.type === QuestionType.GROUPED_QUESTION) {
          for (let z = 0; z < examPartQuestions[j].children?.length; z++) {
            if (examPartQuestions[j].children[z].id === editItemId) {
              isFindQuestion = true;
              examPartQuestions[j].children.splice(z, 1);
              z--;
            }

            if (isFindQuestion && z >= 0) {
              examPartQuestions[j].children[z] = {
                ...examPartQuestions[j].children[z],
                question: {
                  ...examPartQuestions[j].children[z].question,
                  questionIndex: z,
                },
              };
            }
          }
        }
      }
    }

    dispatch(updateExamPartRequests(examParts));
  }, [dispatch, editItemId, examRequest?.examParts]);

  const addChildExamPartQuestion = useCallback(() => {
    const examParts: ExamPartImportRequest[] = examRequest?.examParts ? cloneDeep(examRequest.examParts) : [];
    let newExamPartQuestion = null;
    for (let i = 0; i < examParts?.length; i++) {
      const examPartQuestions = examParts[i].examPartQuestions;
      for (let j = 0; j < examPartQuestions.length; j++) {
        if (examPartQuestions[j].id === editItemId) {
          newExamPartQuestion = buildNewExamPartQuestion(
            examPartQuestions[j].children?.length ?? 0,
            QuestionType.YES_NO_QUESTION,
          );
          examPartQuestions[j].children.push(newExamPartQuestion);
        }
      }
    }

    dispatch(updateExamPartRequests(examParts));
  }, [dispatch, editItemId, examRequest?.examParts]);

  const addNextExamPartQuestion = useCallback(() => {
    const examParts: ExamPartImportRequest[] = examRequest?.examParts ? cloneDeep(examRequest.examParts) : [];
    let isFindQuestion = false;
    let isQuestionInExamPart = false;
    let newExamPartQuestion = null;
    for (let i = 0; i < examParts?.length; i++) {
      const examPartQuestions = examParts[i].examPartQuestions;
      for (let j = 0; j < examPartQuestions.length; j++) {
        if (examPartQuestions[j].id === editItemId) {
          isFindQuestion = true;
          isQuestionInExamPart = true;
          newExamPartQuestion = buildNewExamPartQuestion(j + 1, examPartQuestions[j].question.type as QuestionType);

          if (examPartQuestions.length - 1 === j) {
            examPartQuestions.push(newExamPartQuestion);
          } else {
            examPartQuestions.splice(j + 1, 0, newExamPartQuestion);
          }
          j++;
        }

        if (isFindQuestion && isQuestionInExamPart) {
          examPartQuestions[j].question.questionIndex = j;
        } else if (examPartQuestions[j].question.type === QuestionType.GROUPED_QUESTION) {
          for (let z = 0; z < examPartQuestions[j].children?.length; z++) {
            if (examPartQuestions[j].children[z].id === editItemId) {
              isFindQuestion = true;
              newExamPartQuestion = buildNewExamPartQuestion(
                z + 1,
                examPartQuestions[j].children[z].question.type as QuestionType,
              );
              if (examPartQuestions[j].children.length - 1 === z) {
                examPartQuestions[j].children.push(newExamPartQuestion);
              } else {
                examPartQuestions[j].children.splice(z + 1, 0, newExamPartQuestion);
              }
              z++;
            }

            if (isFindQuestion) {
              examPartQuestions[j].children[z] = {
                ...examPartQuestions[j].children[z],
                question: { ...examPartQuestions[j].children[z].question, questionIndex: z },
              };
            }
          }
        }
      }
    }

    dispatch(updateExamPartRequests(examParts));
  }, [dispatch, editItemId, examRequest?.examParts]);

  const extensionFeature = useMemo(() => {
    switch (modeEditView) {
      case EditDetailTestTemplateView.EDIT_EXAM:
        return null;
      case EditDetailTestTemplateView.EDIT_PATH:
        return null;
      case EditDetailTestTemplateView.EDIT_QUESTION:
      case EditDetailTestTemplateView.EDIT_SUB_QUESTION:
        return (
          <div className="flex items-center justify-center gap-2">
            <button
              className="px-3 py-2 bg-[#ddf5ff] rounded w-fit h-fit text-[18px] hover:bg-[#c5eeff]"
              onClick={addPreviousExamPartQuestion}
            >
              Thêm câu hỏi vào phía trước
            </button>
            <button
              className="px-3 py-2 bg-[#ddf5ff] rounded w-fit h-fit text-[18px] hover:bg-[#c5eeff]"
              onClick={addNextExamPartQuestion}
            >
              Thêm câu hỏi vào phía sau
            </button>
            <button
              className={
                'px-3 py-2 bg-[#ddf5ff] rounded w-fit h-fit text-[18px] hover:bg-[#c5eeff] ' +
                (isEditGroupQuestion ? '' : 'hidden')
              }
              onClick={addChildExamPartQuestion}
            >
              Thêm câu hỏi con
            </button>
            <button
              className="px-3 py-2 bg-[#ddf5ff] rounded w-fit h-fit text-[18px] hover:bg-[#c5eeff]"
              onClick={deleteExamPartQuestion}
            >
              Xoá câu hỏi
            </button>
          </div>
        );
    }
  }, [
    addChildExamPartQuestion,
    addNextExamPartQuestion,
    addPreviousExamPartQuestion,
    deleteExamPartQuestion,
    isEditGroupQuestion,
    modeEditView,
  ]);

  const saveExam = async () => {
    if (isLoading) {
      return;
    }
    dispatch(updateExamImportError());
    if (checkIfExamRequestHasAnyError(examImportError)) {
      message.error('Vui lòng kiểm tra các lỗi trước khi lưu đề');
      return;
    }
    dispatch(setLoading(true));
    try {
      await saveExamRequest(examRequest, type === 'practice');
      message.success('Lưu thành công');
      backStepImport();
    } catch (error) {
      processError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const treeData = useMemo(() => {
    if (!examRequest) return [];
    const isEnglishSubject = checkIsEnglishSubject(examEditSubject);
    const examPartQuestionIndexMap = processExamPartQuestionRequestIndexMap(examRequest.examParts);
    const processQuestionIndex = (index: number, examPartQuestion: ExamPartQuestionImportRequest) => {
      if (!isEnglishSubject || examPartQuestion.question.type === QuestionType.GROUPED_QUESTION) {
        return index;
      }
      const indexFromMap = examPartQuestionIndexMap.get(examPartQuestion.id);
      return indexFromMap !== undefined ? indexFromMap : index;
    };

    const examPartQuestionToTreeItem = (
      examPartQuestion: ExamPartQuestionImportRequest,
      index: number,
      questionErrorMap?: Map<string, ExamPartQuestionErrorCode[]>,
    ): BaseTreeItem => {
      const questionIndex = processQuestionIndex(index, examPartQuestion);
      return {
        title:
          examPartQuestion.question.type === QuestionType.GROUPED_QUESTION
            ? `Câu hỏi nhóm ${questionIndex + 1}`
            : `Câu ${questionIndex + 1}`,
        item: examPartQuestion,
        children:
          examPartQuestion.children?.length > 0
            ? examPartQuestion.children.map((child, index) =>
                examPartQuestionToTreeItem(child, index, questionErrorMap),
              )
            : [],
        errors: questionErrorMap?.get(examPartQuestion.id),
      };
    };
    const examPartToTreeItem = (examPart: ExamPartImportRequest, index: number): BaseTreeItem => {
      const examPartError = examImportError?.examPartErrorMap.get(examPart.id);
      return {
        title: `Phần ${convertToRoman(index + 1)}`,
        item: examPart,
        children:
          examPart.examPartQuestions.length > 0
            ? examPart.examPartQuestions.map((examPartQuestion, index) =>
                examPartQuestionToTreeItem(examPartQuestion, index, examPartError?.examPartQuestionErrorMap),
              )
            : [],
        errors: examPartError?.errorCodes,
      };
    };
    return examRequest.examParts.map((examPart, index) => examPartToTreeItem(examPart, index));
  }, [examRequest]);

  const sortExamPartQuestions = (examPartQuestions: ExamPartQuestionImportRequest[]) => {
    const sortedExamPartQuestions = examPartQuestions.sort(
      (e1, e2) => e1.examPartQuestionIndex - e2.examPartQuestionIndex,
    );
    for (let examPartQuestion of sortedExamPartQuestions) {
      examPartQuestion.question.questionDetails = examPartQuestion.question.questionDetails.sort(
        (q1, q2) => q1.questionDetailIndex - q2.questionDetailIndex,
      );
      sortExamPartQuestions(examPartQuestion.children);
    }
  };

  const loadEditExam = async () => {
    if (!params.examId) {
      return;
    }
    dispatch(updateLoading(true));
    try {
      const examImportRequest =
        type === 'exam' ? await getFullExamDetail(params.examId) : await getFullPracticeDetail(params.examId);
      const examParts = examImportRequest.examParts.sort((e1, e2) => e1.examPartIndex - e2.examPartIndex);

      for (let examPart of examParts) {
        sortExamPartQuestions(examPart.examPartQuestions);
      }
      examImportRequest.examParts = examParts;
      const examTemplate = examImportRequest.examTemplate
        ? await findExamTemplatesById(examImportRequest.examTemplate.id)
        : null;
      const subject =
        examImportRequest && examImportRequest.subject
          ? subjects.find((subject) => subject.id === examImportRequest.subject?.id)
          : null;
      dispatch(
        initExamEdit({
          examImportRequest,
          examTemplate,
          subject: subject || null,
          practice: type === 'practice',
        }),
      );
    } catch (err) {
      processError(err);
    }

    dispatch(updateLoading(false));
  };

  useEffect(() => {
    if (modePage === 'EDIT') {
      loadEditExam().then();
    }
  }, [modePage]);

  const getTreeItemClassName = useCallback(
    (id: string | undefined, hasError: boolean) => {
      return editItemId && editItemId === id
        ? hasError
          ? 'bg-red-600 text-white '
          : 'bg-[#e6f8ff] hover:bg-[#e6f8ff] '
        : hasError
          ? 'bg-D8587E text-white hover:bg-red-600 '
          : 'bg-[#f1f1f1dd] hover:bg-[#e6f8ff] ';
    },
    [editItemId],
  );

  return (
    <div className="w-full h-full bg-white flex flex-col">
      <div className="w-full border-b-1 border-[silver] h-[60px] flex items-center justify-between px-4">
        <button className="px-3 py-2 cursor-pointer rounded w-fit h-fit " onClick={backStepImport}>
          <ArrowLeftOutlined className="text-[20px]" />
        </button>
        {extensionFeature}
      </div>
      <div className="w-full flex gap-4 flex-1">
        <div className="w-[20%] border-r-1 border-[silver] p-2 flex flex-col h-[calc(100vh-210px)] overflow-y-auto">
          <Popover
            content={<ErrorView errors={examImportError?.errorCodes} />}
            title={'Danh sách lỗi'}
            open={!examImportError?.errorCodes.length || examImportError?.errorCodes.length === 0 ? false : undefined}
          >
            <div
              className={
                'text-[22px] font-normal rounded cursor-pointer p-2 pl-1.5 ' +
                getTreeItemClassName(
                  examRequest?.id,
                  !!examImportError?.errorCodes && examImportError?.errorCodes.length > 0,
                )
              }
              onClick={() => selectEditItem(examRequest)}
              dangerouslySetInnerHTML={{ __html: '&nbsp;' + (examRequest?.name ?? '') }}
            ></div>
          </Popover>
          <div className="p-2 flex-1 flex">
            <ul className="overflow-auto flex flex-col gap-y-4 w-full">
              {treeData.map((treeItem) => (
                <ExamMenuItem
                  key={treeItem.item?.id}
                  dataTree={treeItem}
                  selectEditItem={selectEditItem}
                  renderTreeItem={(title, currentTreeItem) => (
                    <Popover
                      content={<ErrorView errors={currentTreeItem.errors} />}
                      title={'Danh sách lỗi'}
                      open={!currentTreeItem.errors || currentTreeItem.errors.length === 0 ? false : undefined}
                    >
                      <div
                        className={
                          'p-2 rounded text-left cursor-pointer ' +
                          getTreeItemClassName(
                            currentTreeItem.item?.id,
                            !!currentTreeItem.errors && currentTreeItem.errors?.length > 0,
                          )
                        }
                        dangerouslySetInnerHTML={{ __html: title }}
                        key={currentTreeItem.item?.id}
                      />
                    </Popover>
                  )}
                />
              ))}
            </ul>
          </div>
        </div>
        <div className="flex-1 h-[calc(100vh-210px)] overflow-y-auto">{editDetailView}</div>
      </div>
      <div className="w-full border-t-1 border-[silver] h-[60px] flex items-center justify-end px-4">
        <button
          className="px-3 py-2 bg-[#c1e0ec] rounded w-fit h-fit text-[18px] hover:bg-[#93b6c5]"
          onClick={saveExam}
        >
          Lưu
        </button>
      </div>
    </div>
  );
}
